import { useMemo, useState } from 'react';
import { ArrowUpDown, TrendingUp, TrendingDown, Minus, Search } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import { Money } from '../../components/Money';
import { METRICS, OPTIONAL_DATA_METRICS, fmtNum, pctDiff } from './utils';

function Delta({ pct, testId }) {
  if (pct === null) return <span className="text-[10px] text-slate-300">yeni</span>;
  const abs = Math.abs(pct);
  let cls = 'text-slate-400';
  let Icon = Minus;
  if (pct >= 0.5) { cls = 'text-emerald-600'; Icon = TrendingUp; }
  else if (pct <= -0.5) { cls = 'text-rose-600'; Icon = TrendingDown; }
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10px] font-semibold ${cls}`} data-testid={testId}>
      <Icon className="h-2.5 w-2.5" />{abs >= 100 ? Math.round(abs) : abs.toFixed(0)}%
    </span>
  );
}

export function MetricValue({ metric, value }) {
  return metric.money ? <Money amount={value} /> : fmtNum(value, metric.decimals || 0);
}

export function DriversTable({ drivers, team, compareMode, selected, onToggle, maxSelect, onOpen }) {
  const [sort, setSort] = useState({ key: 'revenue', dir: -1 });
  const [q, setQ] = useState('');
  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    const list = s ? drivers.filter((d) => d.driver_name.toLowerCase().includes(s)) : drivers;
    return [...list].sort((a, b) => ((a[sort.key] || 0) - (b[sort.key] || 0)) * sort.dir);
  }, [drivers, sort, q]);

  const baseline = (d, m) => (compareMode === 'team' ? m.team(team) : (d.previous ? d.previous[m.key] : null));

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-hidden" data-testid="drivers-table">
      <div className="flex items-center gap-2 p-3 border-b border-slate-100 dark:border-slate-800">
        <Search className="h-4 w-4 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Şoför ara…" className="h-8 max-w-xs" data-testid="drivers-search" />
        <span className="ml-auto text-xs text-slate-500">{selected.length}/{maxSelect} seçili · kıyaslamak için işaretleyin</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800/60 text-[11px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="w-8 px-3 py-2" />
              <th className="text-left px-3 py-2 sticky left-0 bg-slate-50 dark:bg-slate-800">Şoför</th>
              {METRICS.map((m) => (
                <th key={m.key} className="text-right px-3 py-2 whitespace-nowrap">
                  <button type="button" className="inline-flex items-center gap-1 hover:text-slate-900 dark:hover:text-white" onClick={() => setSort((s) => ({ key: m.key, dir: s.key === m.key ? -s.dir : -1 }))} data-testid={`drivers-sort-${m.key}`}>
                    {m.label}<ArrowUpDown className="h-3 w-3 opacity-50" />
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((d) => {
              const isSel = selected.includes(d.driver_key);
              return (
                <tr key={d.driver_key} className={`border-t border-slate-100 dark:border-slate-800 ${isSel ? 'bg-sky-50/70 dark:bg-sky-900/20' : 'hover:bg-slate-50 dark:hover:bg-slate-800/40'}`} data-testid={`driver-row-${d.driver_key}`}>
                  <td className="px-3 py-2">
                    <Checkbox checked={isSel} disabled={!isSel && selected.length >= maxSelect} onCheckedChange={() => onToggle(d.driver_key)} data-testid={`driver-select-${d.driver_key}`} />
                  </td>
                  <td className="px-3 py-2 sticky left-0 bg-inherit">
                    <button type="button" onClick={() => onOpen(d)} className="text-left group" data-testid={`driver-open-${d.driver_key}`}>
                      <div className="font-medium text-slate-800 dark:text-slate-100 whitespace-nowrap group-hover:text-sky-600 group-hover:underline underline-offset-2">{d.driver_name || '—'}</div>
                      <div className="text-[10px] text-slate-400 uppercase">{d.customers.join(' · ')}</div>
                    </button>
                  </td>
                  {METRICS.map((m) => {
                    const noData = OPTIONAL_DATA_METRICS.includes(m.key) && !d[m.key];
                    return (
                      <td key={m.key} className="px-3 py-2 text-right whitespace-nowrap tabular-nums">
                        {noData ? <span className="text-slate-300">—</span> : (
                          <>
                            <div className="text-slate-800 dark:text-slate-100"><MetricValue metric={m} value={d[m.key]} /></div>
                            <Delta pct={pctDiff(d[m.key] || 0, baseline(d, m))} testId={`driver-delta-${d.driver_key}-${m.key}`} />
                          </>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr><td colSpan={METRICS.length + 2} className="text-center py-10 text-slate-400" data-testid="drivers-empty">Bu dönemde rota verisi yok</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
